// Where you are on the guide map: a blue dot, inside a faint circle as wide as the phone is unsure of
// the spot, and a button that centres the map on you. If you've already let the site see your location
// the dot comes up by itself, leaving the map where it is; otherwise nothing is asked until you tap.

import { showToast } from "./toast"

const WATCH_OPTIONS = { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
const LOCATE_ZOOM = 15
const PERMISSION_DENIED = 1
// Past this the circle covers the screen anyway
const MAX_ACCURACY_PX = 4000

// Location arrow, pointing up and to the right
const ARROW = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 3.75 3.75 10.5l7.125 2.625L13.5 20.25l6.75-16.5Z" /></svg>'

export class LocateControl {
  constructor(maplibregl) {
    this.maplibregl = maplibregl
    this.watchId = null
    this.position = null
    this.centreOnFix = false
    this.sizeAccuracy = this.sizeAccuracy.bind(this)
  }

  onAdd(map) {
    this.map = map
    this.container = document.createElement("div")
    this.container.className = "maplibregl-ctrl maplibregl-ctrl-group"
    this.container.innerHTML = `<button type="button" class="map-locate-button">${ARROW}</button>`
    this.button = this.container.querySelector("button")
    this.button.addEventListener("click", () => this.locate())
    this.setState("off")

    if (navigator.geolocation) {
      this.showIfAllowed()
    } else {
      this.container.hidden = true
    }
    return this.container
  }

  onRemove() {
    this.stopWatching()
    this.removeDot()
    this.container.remove()
    this.map = null
  }

  async showIfAllowed() {
    try {
      const status = await navigator.permissions?.query({ name: "geolocation" })
      if (status?.state === "granted" && this.map) this.watch()
    } catch {
      // No Permissions API (or no geolocation in it): wait for a tap
    }
  }

  locate() {
    if (this.position) {
      this.centre()
    } else {
      this.centreOnFix = true
      this.setState("locating")
    }
    this.watch()
  }

  watch() {
    if (this.watchId !== null) return

    this.watchId = navigator.geolocation.watchPosition(
      position => this.update(position.coords),
      error => this.fail(error),
      WATCH_OPTIONS
    )
  }

  stopWatching() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId)
    this.watchId = null
  }

  update({ longitude, latitude, accuracy }) {
    if (!this.map) return

    this.position = [ longitude, latitude ]
    this.accuracy = accuracy

    if (!this.marker) {
      const element = document.createElement("div")
      element.className = "map-you"
      element.setAttribute("role", "img")
      element.setAttribute("aria-label", "You are here")
      element.innerHTML = '<span class="map-you-accuracy"></span><span class="map-you-pulse"></span><span class="map-you-dot"></span>'
      this.accuracyCircle = element.querySelector(".map-you-accuracy")
      this.marker = new this.maplibregl.Marker({ element }).setLngLat(this.position).addTo(this.map)
      // Straight after the map itself, under every pin, so a venue you're standing at stays tappable
      this.map.getCanvas().after(element)
      this.map.on("zoom", this.sizeAccuracy)
    }

    this.marker.setLngLat(this.position)
    this.sizeAccuracy()
    this.setState("active")

    if (this.centreOnFix) {
      this.centreOnFix = false
      this.centre()
    }
  }

  // The circle's radius is the accuracy in metres, at this zoom and latitude (MapLibre's world is
  // 512px across at zoom 0)
  sizeAccuracy() {
    if (!this.accuracyCircle) return

    const metresPerPixel = 40075016.686 * Math.cos(this.position[1] * Math.PI / 180) / (512 * 2 ** this.map.getZoom())
    const diameter = Math.min(Math.round(2 * this.accuracy / metresPerPixel), MAX_ACCURACY_PX)
    this.accuracyCircle.style.width = this.accuracyCircle.style.height = `${diameter}px`
  }

  removeDot() {
    this.marker?.remove()
    this.map?.off("zoom", this.sizeAccuracy)
    this.marker = this.accuracyCircle = this.position = null
  }

  centre() {
    this.map.easeTo({ center: this.position, zoom: Math.max(this.map.getZoom(), LOCATE_ZOOM), duration: 600 })
  }

  fail(error) {
    const denied = error.code === PERMISSION_DENIED
    // Once the dot is up, a lost fix now and then isn't worth mentioning: it stays where you were
    if (this.position && !denied) return

    const asked = this.centreOnFix
    this.centreOnFix = false
    this.stopWatching()
    this.removeDot()
    this.setState("off")

    if (asked) showToast(denied ? "Location is off for this site" : "Couldn't find where you are")
  }

  setState(state) {
    this.button.dataset.state = state
    const label = state === "active" ? "Centre the map on you" : "Show where you are"
    this.button.title = label
    this.button.setAttribute("aria-label", label)
  }
}
